import type { UseQueryResult } from "@tanstack/react-query";
import {
  type KeyboardEvent,
  type Ref,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { MetaList } from "@/profitability/meta-list";
import { isMissing } from "@/profitability/missing";
import { UploadInputButton } from "@/profitability/records/record-view/upload-input-button";
import type { TemplateSetting } from "@/profitability/settings/types";
import type {
  ReportColumn,
  ReportDates,
  ReportRow,
} from "@/profitability/types";
import { rootFigures } from "./figure-roots";
import {
  type FigureCursor,
  sameCursor,
  treeKey,
  visibleItems,
} from "./figure-tree";
import { SourceFigureRow } from "./source-figure-row";
import { SourcePartRow } from "./source-part-row";
import { figureDiffers, figureParts } from "./sourced";
import type { CitationsAnswer } from "./types";

const keyOf = (column: ReportColumn | null) => column?.key ?? "";
const itemId = ({ key, part }: FigureCursor) => `${key}:${part ?? ""}`;

/**
 * The record's figures that can say how they were made, as a tree: each
 * figure with its value and how it was made, its parts under it when open.
 * Up and down move through what is shown, right opens a figure and steps
 * into its parts, left steps back, Enter opens a figure or follows a part
 * (to the figure it is, or the Settings). Whatever is chosen is what the
 * file beside it shows.
 */
export const SourceFigures = ({
  ref,
  reportId,
  runId,
  entries,
  columns,
  row,
  results,
  active,
  expanded,
  settings,
  settingsHref,
  upload,
  onActive,
  onExpand,
  onOpen,
  onLeave,
}: {
  /** The tree, so the dialog can open with its chosen figure focused. */
  ref?: Ref<HTMLUListElement>;
  reportId: string;
  runId: string;
  entries: (ReportColumn | null)[];
  /** Every column of the dataset, to write another figure's value. */
  columns: ReportColumn[];
  row: ReportRow;
  /** Each entry's figure, in the same order. */
  results: UseQueryResult<CitationsAnswer>[];
  active: FigureCursor;
  expanded: ReadonlySet<string>;
  /** The settings the run read, to write a setting's value. */
  settings: TemplateSetting[];
  settingsHref: string;
  /** Where a figure nothing matched can be uploaded; null when it cannot. */
  upload: { dates: ReportDates | null } | null;
  onActive: (cursor: FigureCursor) => void;
  onExpand: (key: string, open: boolean) => void;
  /** Opens one figure and closes the rest: chosen with the pointer, or followed. */
  onOpen: (key: string) => void;
  onLeave: () => void;
}) => {
  const navigate = useNavigate();
  const nodes = useRef(new Map<string, HTMLLIElement>());
  // Moved to by the keyboard: focused once it is drawn.
  const [pending, setPending] = useState<string | null>(null);
  useEffect(() => {
    if (!pending) return;
    nodes.current.get(pending)?.focus();
    setPending(null);
  }, [pending]);

  const keys = entries.map(keyOf);
  const answerOf = (key: string) => results[keys.indexOf(key)]?.data;
  const partsOf = (key: string) => figureParts(answerOf(key));
  const partCount = (key: string) => partsOf(key).length;
  const items = visibleItems(keys, partCount, expanded);
  const columnOf = (key: string | null) =>
    entries.find((column) => !!key && column?.key === key) ?? null;
  // What a missing figure waits on: its missing operands, followed through
  // their own figures to the ones missing for a reason of their own.
  const missingDeps = (key: string) =>
    (answerOf(key)?.figure?.parts ?? []).flatMap((part) =>
      part.used && part.figure && isMissing(part.value) ? [part.figure] : [],
    );
  const rootsOf = (key: string) =>
    rootFigures(key, missingDeps)
      .filter((root) => root !== key)
      .map((root) => {
        const found = columns.find((item) => item.key === root);
        return found?.label ?? root;
      });

  const move = (to: FigureCursor) => {
    onActive(to);
    setPending(itemId(to));
  };
  // To another figure of the record: chosen, the only one open, focused.
  const follow = (key: string) => {
    onOpen(key);
    move({ key, part: null });
  };
  const activate = (at: FigureCursor) => {
    if (at.part === null) return onExpand(at.key, !expanded.has(at.key));
    const part = partsOf(at.key)[at.part];
    if (part?.figure && columnOf(part.figure)) follow(part.figure);
    else if (part?.setting) {
      onLeave();
      navigate(settingsHref);
    }
  };
  const keyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    // A key pressed on a button inside a row is the button's.
    if ((event.target as HTMLElement).getAttribute("role") !== "treeitem")
      return;
    const action = treeKey({
      key: event.key,
      items,
      active,
      partCount,
      expanded,
    });
    if (!action) return;
    event.preventDefault();
    if (action.type === "move") move(action.to);
    else if (action.type === "toggle") onExpand(action.key, action.open);
    else activate(action.at);
  };

  const differing = entries.filter((column, index) =>
    figureDiffers(
      results[index]?.data?.figure,
      column ? row[column.key] : undefined,
      column,
    ),
  ).length;
  const missing = entries.filter(
    (column) => column && isMissing(row[column.key] ?? null),
  ).length;

  return (
    <Stack gap={0} align="stretch" className="min-h-0 flex-1">
      <Stack gap={0.5} className="flex-none px-5 pt-4 pb-2">
        <Text variant="label" render={<h3 />}>
          How each figure was made
        </Text>
        <Text variant="caption" tone="muted" render={<div />}>
          <MetaList>
            {`${entries.length} ${entries.length === 1 ? "figure" : "figures"}`}
            {missing > 0 && `${missing} missing`}
            {differing > 0 &&
              `${differing} ${differing === 1 ? "differs" : "differ"} from the file`}
          </MetaList>
        </Text>
      </Stack>
      <ul
        ref={ref}
        role="tree"
        aria-label="How each figure was made"
        onKeyDown={keyDown}
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-3"
      >
        {entries.map((column, index) => {
          const key = keyOf(column);
          const result = results[index];
          const parts = figureParts(result?.data);
          const open = expanded.has(key);
          const how = result?.data?.figure?.how ?? "cell";
          const count = result?.data?.figure?.count ?? parts.length;
          return (
            <SourceFigureRow
              key={key}
              ref={(node) => {
                if (node) nodes.current.set(itemId({ key, part: null }), node);
                else nodes.current.delete(itemId({ key, part: null }));
              }}
              column={column}
              report={column ? row[column.key] : undefined}
              answer={result?.data}
              loading={!!result?.isPending}
              error={!!result?.isError}
              selected={active.key === key}
              focused={sameCursor(active, { key, part: null })}
              focusable={sameCursor(active, { key, part: null })}
              expanded={parts.length ? open : undefined}
              roots={
                column && isMissing(row[column.key] ?? null)
                  ? rootsOf(key)
                  : undefined
              }
              action={
                upload &&
                column?.source && (
                  <UploadInputButton
                    reportId={reportId}
                    source={column.source}
                    dates={upload.dates}
                    runId={runId}
                    variant="outline"
                  />
                )
              }
              onSelect={() => {
                if (active.key === key && active.part === null)
                  onExpand(key, !open);
                else {
                  onActive({ key, part: null });
                  onOpen(key);
                }
              }}
            >
              {open && parts.length > 0 && (
                <ul
                  role="group"
                  className="mt-0.5 mb-1 ml-4 flex flex-col gap-0.5 border-l border-line pl-2"
                >
                  {parts.map((part, at) => {
                    const cursor = { key, part: at };
                    const operand =
                      columns.find((item) => item.key === part.figure) ?? null;
                    const follows = part.figure ? columnOf(part.figure) : null;
                    return (
                      <SourcePartRow
                        key={at}
                        ref={(node) => {
                          if (node) nodes.current.set(itemId(cursor), node);
                          else nodes.current.delete(itemId(cursor));
                        }}
                        how={how}
                        part={part}
                        column={column}
                        operand={operand}
                        setting={settings.find(
                          (item) => item.key === part.setting,
                        )}
                        settingsHref={settingsHref}
                        active={sameCursor(active, cursor)}
                        focusable={sameCursor(active, cursor)}
                        onSelect={() => onActive(cursor)}
                        onFollow={
                          follows ? () => follow(follows.key) : undefined
                        }
                        onLeave={onLeave}
                      />
                    );
                  })}
                  {count > parts.length && (
                    <li role="none" className="px-2.5 py-1">
                      <Text variant="caption" tone="muted">
                        Showing {parts.length} of {count} lines
                      </Text>
                    </li>
                  )}
                </ul>
              )}
            </SourceFigureRow>
          );
        })}
      </ul>
    </Stack>
  );
};
